/**
 * Organisation Settings — Flight Data Pipeline
 *
 * Configures how drone imagery is handled after a survey mission:
 *   • default_mode          — upload pre-processed outputs / raw images / both
 *   • processing_engine     — who does the photogrammetric processing (external tool,
 *                             on-platform ODM, or future platform pipeline)
 *   • raw_format            — JPEG / DNG / TIFF coming off the drone SD card
 *   • allow_per_mission_override — whether operators can deviate per mission
 *
 * Persisted to organizations.settings->'image_pipeline' via
 * PATCH /api/organizations/:id/settings.
 * The useOrgSettings hook exposes it as `imagePipeline` on every screen.
 */
import React, { useState, useEffect } from 'react';
import { Save, RotateCcw, Info, HardDrive, Cpu, ImageIcon, ToggleLeft, ToggleRight } from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';
import Spinner from '../../components/common/Spinner';
import {
  useAuthStore,
  DEFAULT_IMAGE_PIPELINE,
  OrgImagePipelineSettings,
  ImagePipelineMode,
  ImageProcessingEngine,
  RawImageFormat,
} from '../../store/authStore';
import { organizationsApi } from '../../services/api';

// ─── Option definitions ───────────────────────────────────────────────────────

const MODE_OPTIONS: {
  value: ImagePipelineMode;
  label: string;
  description: string;
  badge?: string;
}[] = [
  {
    value: 'upload_processed',
    label: 'Upload processed outputs only',
    description:
      'Operators process imagery externally (Pix4D, Agisoft, DJI Terra, ODM) and upload finished orthomosaics, GeoTIFFs, point clouds, or DEMs. This is the most common enterprise workflow.',
    badge: 'Default',
  },
  {
    value: 'upload_raw',
    label: 'Upload raw drone images for platform processing',
    description:
      'Operators upload raw JPEGs, DNGs, or TIFFs directly from the drone SD card. The platform (or a connected ODM engine) handles photogrammetric reconstruction before analysis.',
  },
  {
    value: 'upload_both',
    label: 'Upload both raw images and processed outputs',
    description:
      'Full audit trail — raw images are stored alongside processed outputs for verification, re-processing, or regulatory compliance.',
  },
];

const ENGINE_OPTIONS: {
  value: ImageProcessingEngine;
  label: string;
  description: string;
  onlyFor: ImagePipelineMode[];
}[] = [
  {
    value: 'external',
    label: 'External tool (Pix4D, Agisoft, DJI Terra, ODM CLI…)',
    description: 'Processing happens outside the platform before upload. The platform receives finished outputs only.',
    onlyFor: ['upload_processed', 'upload_both'],
  },
  {
    value: 'odm',
    label: 'OpenDroneMap (ODM) — connected instance',
    description:
      'Raw images are forwarded to a connected ODM server for automated photogrammetric reconstruction. Outputs are ingested automatically when the ODM job completes.',
    onlyFor: ['upload_raw', 'upload_both'],
  },
  {
    value: 'platform',
    label: 'Platform-native pipeline (coming soon)',
    description: 'Future built-in processing pipeline. Selecting this now reserves the setting for when the feature is enabled for your organisation.',
    onlyFor: ['upload_raw', 'upload_both'],
  },
];

const FORMAT_OPTIONS: { value: RawImageFormat; label: string; hint: string }[] = [
  { value: 'jpeg', label: 'JPEG (.jpg / .jpeg)', hint: 'DJI, Autel, Parrot — standard compressed format. Smaller files, fastest upload.' },
  { value: 'dng',  label: 'DNG (.dng)',          hint: 'DJI raw format. Larger files with full radiometric data — best for multispectral or precision photogrammetry.' },
  { value: 'tiff', label: 'TIFF (.tif / .tiff)', hint: 'Multispectral and thermal sensors (MicaSense, FLIR). Lossless, per-band files.' },
];

// ─── Helper components ────────────────────────────────────────────────────────

function SectionHeader({ label, hint }: { label: string; hint?: string }) {
  return (
    <div className="mb-4">
      <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
        {label}
      </h2>
      {hint && (
        <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500 flex items-start gap-1">
          <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />{hint}
        </p>
      )}
    </div>
  );
}

function RadioCard<T extends string>({
  option, selected, onSelect,
}: {
  option: { value: T; label: string; description: string; badge?: string };
  selected: boolean;
  onSelect: (v: T) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(option.value)}
      className={[
        'w-full text-left p-4 rounded-xl border-2 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500',
        selected
          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600',
      ].join(' ')}
    >
      <div className="flex items-start gap-3">
        <span className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${selected ? 'border-primary-500' : 'border-gray-400'}`}>
          {selected && <span className="w-2 h-2 rounded-full bg-primary-500 block" />}
        </span>
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2 flex-wrap">
            {option.label}
            {option.badge && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
                {option.badge}
              </span>
            )}
          </span>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
            {option.description}
          </p>
        </div>
      </div>
    </button>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function FlightDataPipelineSettingsPage() {
  const { currentOrganizationId, orgImagePipeline, setOrgImagePipeline } = useAuthStore();

  const [form, setForm]       = useState<OrgImagePipelineSettings>({ ...DEFAULT_IMAGE_PIPELINE });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // ── Load ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      if (!currentOrganizationId) return;
      setLoading(true);
      try {
        const res = await organizationsApi.getMyOrg(currentOrganizationId);
        const org = res.data ?? res;
        const saved: Partial<OrgImagePipelineSettings> = org?.settings?.image_pipeline ?? {};
        setForm({ ...DEFAULT_IMAGE_PIPELINE, ...saved });
      } catch {
        setForm({ ...orgImagePipeline });
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganizationId]);

  const setField = <K extends keyof OrgImagePipelineSettings>(k: K, v: OrgImagePipelineSettings[K]) =>
    setForm(prev => ({ ...prev, [k]: v }));

  // When mode changes, auto-select a sensible engine default
  const handleModeChange = (mode: ImagePipelineMode) => {
    setForm(prev => ({
      ...prev,
      default_mode:      mode,
      processing_engine: mode === 'upload_processed' ? 'external' : prev.processing_engine === 'external' ? 'odm' : prev.processing_engine,
    }));
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!currentOrganizationId) return;
    setSaving(true); setError(null); setSuccess(null);
    try {
      await organizationsApi.updateSettings(currentOrganizationId, { image_pipeline: form });
      setOrgImagePipeline(form);
      setSuccess('Pipeline settings saved — all Flight Analysis and Mission Ingestion screens will use this configuration.');
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.response?.data?.message ?? 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => setForm({ ...DEFAULT_IMAGE_PIPELINE });

  // Engine options filtered to those relevant for the current mode
  const relevantEngines = ENGINE_OPTIONS.filter(e => e.onlyFor.includes(form.default_mode));

  if (loading) return <div className="flex justify-center py-16"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-6 max-w-3xl">

      {/* ── Header ── */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <HardDrive className="w-6 h-6 text-indigo-500" /> Flight Data Pipeline
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Configure how drone imagery is handled after each survey mission.
          This setting applies as the default across all verticals — operators can override it per mission if allowed below.
        </p>
      </div>

      {error   && <Alert type="error"   onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ── Upload mode ── */}
      <Card>
        <SectionHeader
          label="Default Upload Mode"
          hint="Determines what operators are expected to upload after a completed drone survey."
        />
        <div className="space-y-3">
          {MODE_OPTIONS.map(opt => (
            <RadioCard
              key={opt.value}
              option={opt}
              selected={form.default_mode === opt.value}
              onSelect={handleModeChange}
            />
          ))}
        </div>
      </Card>

      {/* ── Processing engine — only shown when raw upload is involved ── */}
      {form.default_mode !== 'upload_processed' && (
        <Card>
          <SectionHeader
            label="Processing Engine"
            hint="Where photogrammetric reconstruction happens when raw images are uploaded."
          />
          <div className="space-y-3">
            {relevantEngines.map(opt => (
              <RadioCard
                key={opt.value}
                option={opt}
                selected={form.processing_engine === opt.value}
                onSelect={(v) => setField('processing_engine', v)}
              />
            ))}
          </div>
        </Card>
      )}

      {/* ── Raw image format — only shown when raw upload is involved ── */}
      {form.default_mode !== 'upload_processed' && (
        <Card>
          <SectionHeader
            label="Raw Image Format"
            hint="The file format coming off the drone SD card. Used to show the correct file-type guidance on Mission Ingestion."
          />
          <div className="space-y-2">
            {FORMAT_OPTIONS.map(opt => (
              <label
                key={opt.value}
                className={[
                  'flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all',
                  form.raw_format === opt.value
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600',
                ].join(' ')}
              >
                <input
                  type="radio"
                  name="raw_format"
                  value={opt.value}
                  checked={form.raw_format === opt.value}
                  onChange={() => setField('raw_format', opt.value)}
                  className="mt-0.5 accent-primary-500 flex-shrink-0"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-1.5">
                    <ImageIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    {opt.label}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{opt.hint}</p>
                </div>
              </label>
            ))}
          </div>
        </Card>
      )}

      {/* ── Per-mission override ── */}
      <Card>
        <SectionHeader
          label="Operator Override"
          hint="Whether individual operators can change the pipeline mode when creating or ingesting a specific mission."
        />
        <button
          type="button"
          onClick={() => setField('allow_per_mission_override', !form.allow_per_mission_override)}
          className="flex items-center gap-3 w-full text-left p-4 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {form.allow_per_mission_override
            ? <ToggleRight className="w-7 h-7 text-primary-500 flex-shrink-0" />
            : <ToggleLeft  className="w-7 h-7 text-gray-400 flex-shrink-0" />
          }
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {form.allow_per_mission_override ? 'Override allowed' : 'Org default enforced'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {form.allow_per_mission_override
                ? 'Operators can change the upload mode per mission. The org default pre-selects but does not lock the choice.'
                : 'The org default is enforced. Operators cannot change the upload mode when creating or ingesting a mission.'}
            </p>
          </div>
        </button>
      </Card>

      {/* ── Summary preview ── */}
      <Card>
        <SectionHeader label="Configuration Summary" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { icon: HardDrive, label: 'Upload Mode',  value: MODE_OPTIONS.find(m => m.value === form.default_mode)?.label.split(' ')[1] ?? form.default_mode },
            { icon: Cpu,       label: 'Processing',   value: form.default_mode === 'upload_processed' ? 'External only' : ENGINE_OPTIONS.find(e => e.value === form.processing_engine)?.label.split(' ')[0] ?? form.processing_engine },
            { icon: ImageIcon, label: 'Raw Format',   value: form.default_mode === 'upload_processed' ? 'N/A' : form.raw_format.toUpperCase() },
            { icon: form.allow_per_mission_override ? ToggleRight : ToggleLeft,
              label: 'Per-mission Override',
              value: form.allow_per_mission_override ? 'Allowed' : 'Locked' },
          ].map(({ icon: Icon, label, value }) => (
            <div key={label} className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-1.5 mb-1">
                <Icon className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">{label}</span>
              </div>
              <p className="text-sm font-bold text-gray-900 dark:text-white">{value}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* ── Actions ── */}
      <div className="flex items-center gap-3">
        <Button onClick={handleSave} loading={saving} disabled={saving}>
          <Save className="w-4 h-4 mr-1.5" />Save Settings
        </Button>
        <Button variant="outline" onClick={handleReset} disabled={saving}>
          <RotateCcw className="w-4 h-4 mr-1.5" />Reset to Defaults
        </Button>
      </div>
    </div>
  );
}

// Made with Bob
